import React, { useEffect, useState, useRef } from "react";
import { NotesNodeData } from "../../types";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";

interface NotesConfigBoxProps {
  data: NotesNodeData;
  onUpdate?: (data: Partial<NotesNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  isDisabled?: boolean; // Add isDisabled prop
  portalRoot?: HTMLElement | null;
}

const NOTE_COLORS = [
  { value: 'yellow', label: 'Yellow', class: 'bg-yellow-200 dark:bg-yellow-800' },
  { value: 'blue', label: 'Blue', class: 'bg-blue-200 dark:bg-blue-800' },
  { value: 'green', label: 'Green', class: 'bg-green-200 dark:bg-green-800' },
  { value: 'red', label: 'Red', class: 'bg-red-200 dark:bg-red-800' },
  { value: 'purple', label: 'Purple', class: 'bg-purple-200 dark:bg-purple-800' },
];

export default function NotesConfigBox({
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  isAdvancedMode = false,
  isDisabled = false, // Destructure isDisabled with default
}: NotesConfigBoxProps) {
  // Initialize state with props
  const [name, setName] = useState(data.name || "");
  const [notes, setNotes] = useState(data.notes || "");
  const [color, setColor] = useState(data.color || "yellow");
  
  // Initialize visibility settings
  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(
    data.nameVisibility === "hidden" ? "hidden" : "visible"
  );
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(
    data.notesVisibility === "hidden" ? "hidden" : "visible"
  );
  
  // Track if this is the first render
  const isFirstRender = useRef(true);
  
  // Track previous update to prevent loops
  const prevUpdateRef = useRef("");
  
  // Initialize state from props on first render only
  useEffect(() => {
    if (isFirstRender.current) {
      setName(data.name || "");
      setNotes(data.notes || "");
      setColor(data.color || "yellow");
      
      // Initialize visibility settings
      setNameVisibility(data.nameVisibility === "hidden" ? "hidden" : "visible");
      setNotesVisibility(data.notesVisibility === "hidden" ? "hidden" : "visible");
      
      isFirstRender.current = false;
    }
  }, [data]);
  
  // Update parent when state changes
  useEffect(() => {
    // Skip the first render to avoid unnecessary updates
    if (isFirstRender.current) return;
    
    const updates = {
      name,
      notes,
      color,
      nameVisibility,
      notesVisibility
    };
    
    const updatesJson = JSON.stringify(updates);
    if (updatesJson !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = updatesJson;
      onUpdate(updates);
    }
  }, [
    name, 
    notes, 
    color,
    nameVisibility,
    notesVisibility,
    onUpdate
  ]);

  // Toggle visibility setting
  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Title
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Note title"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
          disabled={isDisabled}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes here"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[120px] max-h-[400px]"
          maxLength={1000} // Increased character limit for notes
          disabled={isDisabled}
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/1000
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Note Color
        </label>
        <div className="grid grid-cols-5 gap-2">
          {NOTE_COLORS.map((noteColor) => (
            <button
              key={noteColor.value}
              type="button"
              onClick={() => setColor(noteColor.value)}
              className={`w-full h-4 rounded-md transition-all ${noteColor.class} ${
                color === noteColor.value
                  ? "ring-2 ring-offset-2 ring-pink-500 dark:ring-offset-gray-800"
                  : ""
              }`}
              title={noteColor.label}
              disabled={isDisabled}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
